import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import TileLayer from 'ol/layer/Tile';
import VectorSource from 'ol/source/Vector';
import IconAnchorUnits from 'ol/style/IconAnchorUnits';
import { Map, Overlay } from 'ol';
import { Vector } from 'ol/layer';
import { Icon, Style } from 'ol/style';
import { POPUP_ACTIVE, POPUP_CLICK_CALLBACK, POPUP_CONTENT, POPUP_ID } from './feature-keys';

export interface TileLayerOption {
  url?: string;
  attrs?: string;
  source?: typeof XYZ;
}

export const createIconStyle = (src: string, img: HTMLImageElement | HTMLCanvasElement = undefined) => {
  return new Style({
    image: new Icon({
      anchor: [0.5, 46],
      anchorXUnits: IconAnchorUnits.FRACTION,
      anchorYUnits: IconAnchorUnits.PIXELS,
      src,
      img,
      imgSize: img ? [img.width, img.height] : undefined,
    }),
  });
};

export const createFeature = (point: Point) => {
  return new Feature(point);
};

export const createIconVector = (style: Style, feature: Feature) => {
  return new Vector({
    style,
    source: new VectorSource({ features: [feature] })
  })
};

export const createPopupOverlay = (feature: Feature) => {
  const body = document.getElementsByTagName('body')[0];
  const container = document.createElement('div');
  container.className = 'ol-marker-popup-container';
  const id = feature.get(POPUP_ID);
  container.id = id;
  container.innerHTML = `
    <div class="ol-marker-popup">
      ${feature.get(POPUP_CONTENT)}
    </div>
  `;
  body.appendChild(container);
  const overlay = new Overlay({
    element: container,
    autoPan: true,
    autoPanAnimation: {
      duration: 500
    },
    stopEvent: false
  });
  overlay.set(POPUP_ID, id);
  return overlay;
}

export const createTileLayer = (options?: TileLayerOption) => {
  if (!options) options = {} as any;
  const tile = new TileLayer({
    source: new (options.source ?? OSM)({
      attributions: options.attrs ?? '',
      url: options.url
    }),
  })
  return tile;
};

export const registerMarkerClickHandlers = (map: Map, overlays: Overlay[], onClick: any) => {
  map.on('click', (event) => {
    hiddenPopups(overlays);
    const feature = map.forEachFeatureAtPixel(event.pixel, f => {
      if (f.get(POPUP_ACTIVE)) return f;
    });
    if (feature) {
      const callback = feature.get(POPUP_CLICK_CALLBACK);
      const coordinates = (feature.getGeometry() as any).getCoordinates();
      const id = feature.get(POPUP_ID);
      const container = document.getElementById(id);
      container.style.display = 'flex';
      for (const overlay of overlays) {
        if (overlay.get(POPUP_ID) === id) {
          overlay.setPosition(coordinates)
          if (onClick) {
            onClick(event);
          }
          break;
        }
      }
      if (callback) {
        if (typeof callback !== 'function') {
          throw new Error('marker click callback must be a function');
        }
        callback(feature);
      }
    }
    else {
      hiddenPopups(overlays);
    }
  });
  map.on('pointermove', function (e) {
    if (e.dragging) {
      hiddenPopups(overlays);
      return;
    }
    const pixel = map.getEventPixel(e.originalEvent);
    const hit = map.hasFeatureAtPixel(pixel);
    const target = document.getElementById(map.getTarget() as string);
    target.style.cursor = hit ? 'pointer' : '';
  });
}

export const fitZoomToMarker = (map: Map, layers: Vector[], padding: number = 100) => {
  const [w, h] = map.getSize();
  padding = padding ?? 100;
  let minx, miny, maxx, maxy;
  for (let i = 0; i < layers.length; i++) {
    const ext = layers[i].getSource().getExtent();
    if (i === 0) {
      minx = ext[0];
      miny = ext[1];
      maxx = ext[2];
      maxy = ext[3];
    }
    else {
      if (ext[0] < minx) minx = ext[0];
      if (ext[1] < miny) miny = ext[1];
      if (ext[2] > maxx) maxx = ext[2];
      if (ext[3] > maxy) maxy = ext[3];
    }
  }
  if (minx) {
    map.getView().fit([minx, miny, maxx, maxy], { size: [w - padding, h - padding] });
  }
}

const hiddenPopups = (overlays: Overlay[]) => {
  overlays.forEach(o => {
    const id = o.get(POPUP_ID);
    const element = document.getElementById(id);
    element.style.display = 'none';
  })
}
